/**
 * Course Structure illustration — miniature wireframe representing a course:
 * course header with gradient banner, module/topic/lesson tree structure below.
 */
import type { IllustrationProps } from './types';
import { usePalette } from './types';

export function CourseStructure({ variant = 'on-gradient', className, style }: IllustrationProps) {
  const p = usePalette(variant);

  return (
    <svg
      viewBox="0 0 120 140"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      style={style}
      aria-hidden="true"
    >
      {/* Card frame */}
      <rect
        x="4"
        y="4"
        width="112"
        height="132"
        rx="10"
        fill={p.frameFill}
        stroke={p.frameStroke}
        strokeWidth="1"
      />

      {/* Course gradient header */}
      <rect x="4" y="4" width="112" height="32" rx="10" fill={p.accent1} opacity="0.3" />
      <rect x="4" y="26" width="112" height="10" fill={p.accent1} opacity="0.3" />
      {/* Course title text in header */}
      <rect x="14" y="14" width="56" height="4" rx="2" fill={p.strong} opacity="0.6" />
      <rect x="14" y="22" width="38" height="3" rx="1.5" fill={p.strong} opacity="0.3" />

      {/* Module 1 — expanded */}
      <rect
        x="14"
        y="44"
        width="92"
        height="8"
        rx="3"
        fill={p.cardFill}
        stroke={p.cardStroke}
        strokeWidth="0.75"
      />
      <rect x="20" y="47" width="36" height="2.5" rx="1.25" fill={p.subtle} />
      {/* Chevron indicator */}
      <path
        d="M100 46 L102 48 L100 50"
        stroke={p.medium}
        strokeWidth="0.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* Topic 1.1 — indented */}
      <rect x="24" y="56" width="82" height="7" rx="3" fill={p.faint} />
      <rect x="30" y="58.5" width="28" height="2" rx="1" fill={p.subtle} />
      {/* Lesson dots */}
      <circle cx="90" cy="59.5" r="1.5" fill={p.accent1} opacity="0.5" />
      <circle cx="95" cy="59.5" r="1.5" fill={p.accent2} opacity="0.5" />
      <circle cx="100" cy="59.5" r="1.5" fill={p.faint} />

      {/* Topic 1.2 — indented */}
      <rect x="24" y="66" width="82" height="7" rx="3" fill={p.faint} />
      <rect x="30" y="68.5" width="34" height="2" rx="1" fill={p.subtle} />
      <circle cx="90" cy="69.5" r="1.5" fill={p.accent1} opacity="0.5" />
      <circle cx="95" cy="69.5" r="1.5" fill={p.faint} />
      <circle cx="100" cy="69.5" r="1.5" fill={p.faint} />

      {/* Module 2 — collapsed */}
      <rect
        x="14"
        y="80"
        width="92"
        height="8"
        rx="3"
        fill={p.cardFill}
        stroke={p.cardStroke}
        strokeWidth="0.75"
      />
      <rect x="20" y="83" width="42" height="2.5" rx="1.25" fill={p.subtle} />
      <path
        d="M99 82.5 L101 84 L99 85.5"
        stroke={p.medium}
        strokeWidth="0.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* Module 3 — collapsed */}
      <rect
        x="14"
        y="92"
        width="92"
        height="8"
        rx="3"
        fill={p.cardFill}
        stroke={p.cardStroke}
        strokeWidth="0.75"
      />
      <rect x="20" y="95" width="30" height="2.5" rx="1.25" fill={p.subtle} />
      <path
        d="M99 94.5 L101 96 L99 97.5"
        stroke={p.medium}
        strokeWidth="0.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* Progress summary bar at bottom */}
      <rect x="14" y="110" width="92" height="3" rx="1.5" fill={p.faint} />
      <rect x="14" y="110" width="52" height="3" rx="1.5" fill={p.accent1} opacity="0.5" />

      {/* Progress label */}
      <rect x="14" y="118" width="28" height="2.5" rx="1.25" fill={p.faint} />
      <rect x="78" y="118" width="28" height="2.5" rx="1.25" fill={p.medium} opacity="0.5" />
    </svg>
  );
}
